import CardRadioForm from '@/global/components/CardRadioForm'
import SelectCompetencia, {
    useCompetencia,
} from '@/global/components/Selects/SelectCompetencia'
import Grid from '@/global/components/ui/Grid'
import { Label } from '@/global/components/ui/label'
import Dialog from '@/global/guides/Dialog'
import dayjs from 'dayjs'
import { useState } from 'react'
import { SubmitHandler } from 'react-hook-form'
import { espelhoPonto } from 'service/api'
import { toast } from 'sonner'
import { useStore } from 'store'
import { z } from 'zod'

const schema = z.object({
    formato: z.enum(['pdf', 'zip']),
})

type FormInput = z.infer<typeof schema>

/** Lista as competências (YYYY-MM) de inicial até final, inclusive. */
function listPeriodo(inicial: string, final: string): string[] {
    const inicio = dayjs(inicial, 'YYYY-MM')
    const fim = dayjs(final, 'YYYY-MM')
    const lista: string[] = []
    let atual = inicio
    while (!atual.isAfter(fim, 'month')) {
        lista.push(atual.format('YYYY-MM'))
        atual = atual.add(1, 'month')
    }
    return lista
}

export default function BaixarPeriodoModal() {
    const funcionarioId = useStore(state => state.employeeId)
    const empresaId = useStore(state => state.companyId)

    const [inicial, setInicial] = useCompetencia()
    const [final, setFinal] = useCompetencia()
    const [loading, setLoading] = useState(false)

    const competencias = listPeriodo(inicial, final)

    const handleSubmit: SubmitHandler<FormInput> = async ({ formato }) => {
        if (competencias.length === 0) {
            return toast.warning(
                'A competência inicial deve ser anterior ou igual à final',
            )
        }

        try {
            const filename = await espelhoPonto.post<
                { competencias: string[]; formato: FormInput['formato'] },
                string
            >(`periodo/${funcionarioId}?empresaId=${empresaId}`, {
                competencias,
                formato,
            })
            return espelhoPonto.calculos.file(`zip/${filename}`)
        } catch (error) {
            throw toast.error(
                error instanceof Error
                    ? error.message
                    : 'Erro ao gerar o arquivo',
            )
        }
    }

    return (
        <Dialog.Root
            height="auto"
            width="md"
            loading={loading}
            onSubmit={v => {
                setLoading(true)
                return (handleSubmit(v) as any).finally(() => setLoading(false))
            }}
            schema={schema}
            defaultValues={{ formato: 'pdf' }}
        >
            <Dialog.Title>Baixar espelhos por período</Dialog.Title>
            <Dialog.Content dividers>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Label>Período</Label>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <SelectCompetencia
                            label="Competência inicial"
                            value={inicial}
                            onChange={setInicial}
                        />{' '}
                        <span className="text-xs text-secondary-foreground">
                            {competencias.length > 0
                                ? `${competencias.length} competência(s) serão geradas.`
                                : 'Selecione um período válido.'}
                        </span>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <SelectCompetencia
                            label="Competência final"
                            value={final}
                            onChange={setFinal}
                        />
                    </Grid>

                    <Grid item xs={12} className="h-8" children={<></>} />

                    <Grid item xs={12}>
                        <CardRadioForm
                            name="formato"
                            label="Formato do download"
                            options={[
                                {
                                    value: 'pdf',
                                    titulo: 'PDF único',
                                    descricao:
                                        'Junta todas as competências em um único arquivo PDF.',
                                },
                                {
                                    value: 'zip',
                                    titulo: 'ZIP',
                                    descricao:
                                        'Um arquivo PDF por competência, compactados em um ZIP.',
                                },
                            ]}
                        />
                    </Grid>
                </Grid>
            </Dialog.Content>
            <Dialog.Actions>
                <Dialog.Actions.SecondaryButton />
                <Dialog.Actions.PrimaryButton
                    closeOnSubmit
                    disabled={!competencias.length}
                >
                    Baixar
                </Dialog.Actions.PrimaryButton>
            </Dialog.Actions>
        </Dialog.Root>
    )
}
